import * as React from 'react';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/molecules/dialog';
import { compressImage } from '@/lib/utils/image';

// ============ Types ============

export type AspectRatio = '1:1' | '16:9' | '4:3';

interface ImageCropperProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  aspectRatio: AspectRatio;
  onCropComplete: (file: File, preview: string) => void;
  minWidth?: number;
  minHeight?: number;
}

// ============ Helpers ============

function getAspectRatioValue(ratio: AspectRatio): number {
  switch (ratio) {
    case '1:1':
      return 1;
    case '16:9':
      return 16 / 9;
    case '4:3':
      return 4 / 3;
    default:
      return 1;
  }
}

function getAspectRatioLabel(ratio: AspectRatio): string {
  switch (ratio) {
    case '1:1':
      return 'Carré (1:1)';
    case '16:9':
      return 'Large (16:9)';
    case '4:3':
      return 'Standard (4:3)';
    default:
      return ratio;
  }
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
): Crop {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

// ============ Component ============

export function ImageCropper({
  open,
  onOpenChange,
  imageSrc,
  aspectRatio,
  onCropComplete,
  minWidth = 1080,
  minHeight = 1080,
}: ImageCropperProps) {
  const [crop, setCrop] = React.useState<Crop>();
  const [completedCrop, setCompletedCrop] = React.useState<PixelCrop>();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);

  const aspect = getAspectRatioValue(aspectRatio);

  // Initialize crop when image loads
  const onImageLoad = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    },
    [aspect]
  );

  // Generate cropped image
  const getCroppedImage = React.useCallback(async (): Promise<{
    file: File;
    preview: string;
  } | null> => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    // Calculate pixel crop dimensions
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const pixelCropX = completedCrop.x * scaleX;
    const pixelCropY = completedCrop.y * scaleY;
    const pixelCropWidth = completedCrop.width * scaleX;
    const pixelCropHeight = completedCrop.height * scaleY;

    // Set canvas size to output dimensions
    // Use minimum dimensions or crop size, whichever is larger
    const outputWidth = Math.max(pixelCropWidth, minWidth);
    const outputHeight = Math.max(pixelCropHeight, minHeight);

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    // Draw the cropped image
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      pixelCropX,
      pixelCropY,
      pixelCropWidth,
      pixelCropHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    // Convert to blob and data URL
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }

          // Create file from blob
          const file = new File([blob], 'cropped-banner.jpg', {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          // Compress if needed
          const compressedFile = await compressImage(file, {
            maxSizeMB: 3,
            maxWidthOrHeight: 1920,
            quality: 0.9,
          });

          // Generate preview as data URL (base64) instead of blob URL
          // This persists correctly across component unmounts and sessionStorage
          const reader = new FileReader();
          reader.onloadend = () => {
            const preview = reader.result as string;
            resolve({ file: compressedFile, preview });
          };
          reader.onerror = () => {
            reject(new Error('Failed to read file as data URL'));
          };
          reader.readAsDataURL(compressedFile);
        },
        'image/jpeg',
        0.9
      );
    });
  }, [completedCrop, minWidth, minHeight]);

  // Handle crop confirmation
  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      const result = await getCroppedImage();
      if (result) {
        onCropComplete(result.file, result.preview);
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    onOpenChange(false);
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Recadrer l'image</DialogTitle>
          <DialogDescription>
            Ajustez le cadrage de votre image. Format: {getAspectRatioLabel(aspectRatio)}
          </DialogDescription>
        </DialogHeader>

        <div className="relative bg-muted rounded-lg overflow-hidden">
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            minWidth={100}
            minHeight={100}
            className="max-h-[60vh]"
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Image à recadrer"
              onLoad={onImageLoad}
              className="max-h-[60vh] w-auto mx-auto"
            />
          </ReactCrop>
        </div>

        <div className="text-xs text-muted-foreground text-center">
          Dimensions minimales recommandées: {minWidth}x{minHeight}px
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isProcessing}
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isProcessing || !completedCrop}
          >
            {isProcessing ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                Traitement...
              </>
            ) : (
              'Valider'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
